// Ponto de partida do app: sessão, rotas e atualização do app instalado.
import { registerSW } from 'virtual:pwa-register';
import './estilos.css';
import { h } from './lib/dom.js';
import { definirRota, iniciarRotas, ir, renderizar } from './rotas.js';
import { detalheDoErro, ehErroDoArmazenamento, mensagemDeErro } from './lib/erros.js';
import { fecharBanco } from './local/banco.js';
import { ligarRegistroDeErros } from './lib/registro-de-erros.js';
import { aoChegarDados, aoPerderPermissao, ehErroDeRede, jaBaixou, ligarSincronia, sincronizar, sincronizarLogo } from './local/sincronia.js';
import {
  carregarVinculos,
  iniciarSessao,
  podeGravar,
  prepararUsuario,
  quandoAContaForConferida,
  sessao,
  sessaoEncerrada,
} from './sessao.js';
import { supabase } from './supabase.js';
import { avisar, carregando, faixa } from './ui/componentes.js';
import { telaAcerto, telaAcertoNovo, telaAcertos } from './telas/acertos.js';
import { telaRelatorioAcertos } from './telas/relatorio.js';
import { telaAPagar } from './telas/apagar.js';
import { telaAssinar, telaAssinarAcerto } from './telas/assinar.js';
import { telaCarga } from './telas/cargas.js';
import { telaCliente, telaClientes } from './telas/clientes.js';
import { telaColaborador, telaColaboradores, telaRegra } from './telas/colaboradores.js';
import { telaCobranca } from './telas/cobranca.js';
import { telaRaioX, telaRaioXDeFora } from './telas/raiox.js';
import { telaTrajeto } from './telas/trajeto.js';
import { telaAgenciadorCarga, telaAgenciadorCargas, telaAgenciadorInicio, telaAgenciadorReceber } from './telas/agenciador.js';
import { telaAjuda } from './telas/ajuda.js';
import { telaResumoDaSemana } from './telas/resumo.js';
import { telaConta } from './telas/conta.js';
import { telaContaFixa, telaContasFixas } from './telas/contasfixas.js';
import { telaDespesa, telaDespesas } from './telas/despesas.js';
import { destinoInicial, telaCodigo, telaEmpresaNova, telaEmpresas, telaEntrar } from './telas/entrada.js';
import { guardarPedido } from './lib/depois-de-entrar.js';
import { telaInicio } from './telas/inicio.js';
import { telaPrivacidade, telaTermos } from './telas/legal.js';
import { telaMais } from './telas/mais.js';
import {
  telaEntrarAgenciador, telaEntrarMotorista, telaMotoristaCarga, telaMotoristaDespesa, telaMotoristaDespesas, telaMotoristaInicio, telaMotoristaSaldo,
  telaMotoristaViagem,
  telaMotoristaViagens, telaSemAcesso,
} from './telas/motorista.js';
import { telaItemManutencao, telaManutencao, telaManutencaoVeiculo } from './telas/manutencao.js';
import { telaOficina, telaOficinas } from './telas/oficinas.js';
import { telaPendencias } from './telas/pendencias.js';
import { telaAReceber } from './telas/receber.js';
import { telaRelatorioCaminhoes, telaRelatorioContador, telaRelatorioRemuneracao, telaRelatorios } from './telas/relatorios.js';
import { telaSos } from './telas/sos.js';
import { telaConvites, telaUsuarios } from './telas/usuarios.js';
import { telaVale } from './telas/vales.js';
import { telaVeiculo, telaVeiculos } from './telas/veiculos.js';
import { telaViagem, telaViagemForm, telaViagens } from './telas/viagens.js';
import { telaIniciarViagem } from './telas/iniciar-viagem.js';

// Registro automático de erros (0.13.0): o erro de qualquer aparelho chega ao
// banco, sem precisar de print (veja lib/registro-de-erros.js).
ligarRegistroDeErros({
  enviar: (registro) => supabase.rpc('registrar_erro', { p_erro: registro }),
  contexto: () => ({ empresa: sessao.empresa?.id ?? null, papel: sessao.papel ?? null }),
  versao: __VERSAO__,
});

definirRota('/entrar', telaEntrar, { publica: true, titulo: 'Entrar' });
definirRota('/codigo', telaCodigo, { publica: true, titulo: 'Código de acesso' });
definirRota('/empresa/nova', telaEmpresaNova, { semEmpresa: true, titulo: 'Cadastro da empresa' });
definirRota('/empresas', telaEmpresas, { semEmpresa: true, titulo: 'Empresas' });
definirRota('/convites', telaConvites, { semEmpresa: true, titulo: 'Convites' });
definirRota('/inicio', telaInicio, { titulo: 'Início' });
definirRota('/veiculos', telaVeiculos, { titulo: 'Veículos' });
definirRota('/veiculos/novo', telaVeiculo, { titulo: 'Novo veículo' });
definirRota('/veiculos/:id', telaVeiculo, { titulo: 'Veículo' });
definirRota('/veiculos/:id/manutencao', telaManutencaoVeiculo, { titulo: 'Manutenção' });
definirRota('/veiculos/:veiculo/manutencao/novo', telaItemManutencao, { titulo: 'Item de manutenção' });
definirRota('/manutencao', telaManutencao, { titulo: 'Manutenção preventiva' });
definirRota('/manutencao/:item', telaItemManutencao, { titulo: 'Item de manutenção' });
definirRota('/colaboradores', telaColaboradores, { titulo: 'Colaboradores' });
definirRota('/colaboradores/novo', telaColaborador, { titulo: 'Novo colaborador' });
definirRota('/colaboradores/:id', telaColaborador, { titulo: 'Colaborador' });
definirRota('/colaboradores/:colaborador/regras/nova', telaRegra, { titulo: 'Nova remuneração' });
definirRota('/regras/:id', telaRegra, { titulo: 'Remuneração' });
definirRota('/clientes', telaClientes, { titulo: 'Clientes' });
definirRota('/clientes/novo', telaCliente, { titulo: 'Novo cliente' });
definirRota('/clientes/:id', telaCliente, { titulo: 'Cliente' });
definirRota('/conta', telaConta, { titulo: 'Conta' });
definirRota('/assinatura', telaCobranca, { titulo: 'Assinatura' });
definirRota('/raiox', telaRaioX, { titulo: 'Raio-X do frete' });
definirRota('/trajeto', telaTrajeto, { titulo: 'Planejar o trajeto' });
definirRota('/usuarios', telaUsuarios, { titulo: 'Usuários' });
definirRota('/pendencias', telaPendencias, { titulo: 'Alterações não enviadas', motoristaTambem: true });
definirRota('/mais', telaMais, { titulo: 'Mais' });
definirRota('/viagens', telaViagens, { titulo: 'Viagens' });
definirRota('/viagens/nova', telaViagemForm, { titulo: 'Nova viagem' });
definirRota('/viagens/iniciar', telaIniciarViagem, { titulo: 'Aceitar e iniciar a viagem' });
definirRota('/viagens/:id', telaViagem, { titulo: 'Viagem' });
definirRota('/viagens/:id/editar', telaViagemForm, { titulo: 'Dados da viagem' });
definirRota('/viagens/:viagem/cargas/nova', telaCarga, { titulo: 'Nova carga' });
definirRota('/cargas/:id', telaCarga, { titulo: 'Carga' });
definirRota('/vales/novo', telaVale, { titulo: 'Novo vale' });
definirRota('/vales/:id', telaVale, { titulo: 'Vale' });
definirRota('/despesas', telaDespesas, { titulo: 'Despesas' });
definirRota('/despesas/nova', telaDespesa, { titulo: 'Nova despesa' });
definirRota('/despesas/:id', telaDespesa, { titulo: 'Despesa' });
definirRota('/apagar', telaAPagar, { titulo: 'A pagar' });
definirRota('/areceber', telaAReceber, { titulo: 'A receber' });
definirRota('/contas-fixas', telaContasFixas, { titulo: 'Contas fixas' });
definirRota('/contas-fixas/nova', telaContaFixa, { titulo: 'Nova conta fixa' });
definirRota('/contas-fixas/:id', telaContaFixa, { titulo: 'Conta fixa' });
definirRota('/acertos', telaAcertos, { titulo: 'Acertos' });
definirRota('/acertos/novo', telaAcertoNovo, { titulo: 'Acerto' });
definirRota('/acertos/relatorio', telaRelatorioAcertos, { titulo: 'Relatório de acertos' });
definirRota('/acertos/:id', telaAcerto, { titulo: 'Acerto' });
definirRota('/relatorios', telaRelatorios, { titulo: 'Relatórios' });
definirRota('/relatorios/caminhoes', telaRelatorioCaminhoes, { titulo: 'Caminhões' });
definirRota('/relatorios/remuneracao', telaRelatorioRemuneracao, { titulo: 'Remuneração por pessoa' });
definirRota('/relatorios/contador', telaRelatorioContador, { titulo: 'Para o contador' });
definirRota('/relatorios/semana', telaResumoDaSemana, { titulo: 'Resumo da semana' });
definirRota('/oficinas', telaOficinas, { titulo: 'Oficinas de confiança' });
definirRota('/oficinas/nova', telaOficina, { titulo: 'Nova oficina' });
definirRota('/oficinas/:id', telaOficina, { titulo: 'Oficina' });
// SOS mecânica: no app do motorista e, para conferir, no do escritório.
definirRota('/sos', telaSos, { titulo: 'SOS mecânica', motoristaTambem: true });
definirRota('/ajuda', telaAjuda, { titulo: 'Ajuda', motoristaTambem: true, agenciadorTambem: true });
// App do motorista (entrou pelo link do WhatsApp, sem e-mail): só estas telas.
definirRota('/m', telaMotoristaInicio, { titulo: 'Início', soMotorista: true });
definirRota('/m/viagens', telaMotoristaViagens, { titulo: 'Minhas viagens', soMotorista: true });
definirRota('/m/viagens/:id', telaMotoristaViagem, { titulo: 'Viagem', soMotorista: true });
definirRota('/m/cargas/:id', telaMotoristaCarga, { titulo: 'Dados da carga', soMotorista: true });
definirRota('/m/saldo', telaMotoristaSaldo, { titulo: 'Saldo e vales', soMotorista: true });
definirRota('/m/despesas', telaMotoristaDespesas, { titulo: 'Minhas despesas', soMotorista: true });
definirRota('/m/despesas/nova', telaMotoristaDespesa, { titulo: 'Nova despesa', soMotorista: true });
definirRota('/m/despesas/:id', telaMotoristaDespesa, { titulo: 'Despesa', soMotorista: true });
definirRota('/m/raiox', telaRaioXDeFora, { titulo: 'Raio-X do frete', soMotorista: true });
definirRota('/m/trajeto', telaTrajeto, { titulo: 'Planejar o trajeto', soMotorista: true });
// App do agenciador (0.21.0, entrou pelo link): só as cargas dele, o que tem a
// receber, os caminhões vazios e o Raio-X.
definirRota('/a', telaAgenciadorInicio, { titulo: 'Início', soAgenciador: true });
definirRota('/a/cargas', telaAgenciadorCargas, { titulo: 'Minhas cargas', soAgenciador: true });
definirRota('/a/cargas/:id', telaAgenciadorCarga, { titulo: 'Carga', soAgenciador: true });
definirRota('/a/receber', telaAgenciadorReceber, { titulo: 'A receber', soAgenciador: true });
definirRota('/a/raiox', telaRaioXDeFora, { titulo: 'Raio-X do frete', soAgenciador: true });
definirRota('/a/trajeto', telaTrajeto, { titulo: 'Planejar o trajeto', soAgenciador: true });
definirRota('/sem-acesso', telaSemAcesso, { semEmpresa: true, titulo: 'Sem acesso' });
// O link do app do motorista: abre sem login (o celular entra sozinho).
definirRota('/motorista/:codigo', telaEntrarMotorista, { livre: true, titulo: 'App do motorista' });
definirRota('/agenciador/:codigo', telaEntrarAgenciador, { livre: true, titulo: 'App do agenciador' });
// Abertas pelo colaborador, pelo link do WhatsApp: sem login e sem empresa.
definirRota('/assinar/:codigo', telaAssinar, { livre: true, titulo: 'Assinar vale' });
definirRota('/assinar-acerto/:codigo', telaAssinarAcerto, { livre: true, titulo: 'Assinar acerto' });
// Termos de uso e Política de privacidade: abrem com ou sem login (LGPD).
definirRota('/termos', telaTermos, { livre: true, titulo: 'Termos de uso' });
definirRota('/privacidade', telaPrivacidade, { livre: true, titulo: 'Política de privacidade' });

const app = document.getElementById('app');

// Dados da empresa no aparelho: na primeira vez, espera baixar tudo; depois,
// abre na hora com o que está guardado e atualiza por trás. Enquanto a
// primeira baixa não acontece, as telas mostrariam a conta vazia (e alguém
// cadastraria tudo de novo): aparece um aviso no lugar.
const empresasProntas = new Set();

const chaveDaEmpresa = () => `${sessao.entrada}:${sessao.empresa?.id}`;

// Devolve o erro da baixa, se não foi falta de sinal.
async function prepararEmpresa(chave) {
  const empresaId = sessao.empresa.id;
  let falha = null;
  try {
    if (await jaBaixou(empresaId)) {
      sincronizarLogo({ puxar: true, atraso: 0 });
    } else {
      falha = (await sincronizar({ puxar: true })).erroNaBaixa ?? null;
    }
    if (await jaBaixou(empresaId)) empresasProntas.add(chave);
  } catch (erro) {
    console.warn('Não foi possível baixar os dados agora:', erro);
    falha = ehErroDeRede(erro) ? null : erro;
  }
  return falha;
}

function telaSemDados(falha) {
  return h('div', { class: 'entrada sem-dados' },
    falha
      ? faixa(`Não foi possível baixar os dados desta conta. ${mensagemDeErro(falha)}`, 'perigo', 'Erro ao baixar os dados')
      : faixa(
        'Os dados desta conta ainda não estão neste aparelho. Na primeira vez é preciso internet para baixá-los; depois, o app funciona sem sinal.',
        'alerta',
        'Sem sinal',
      ),
    h('button', { class: 'botao cheio', type: 'button', onClick: () => renderizar() }, 'Tentar de novo'));
}

// O sinal voltou com o aviso na tela: tenta baixar de novo.
window.addEventListener('online', () => {
  if (app.querySelector('.sem-dados')) renderizar();
});

function desenhar({ caminho, encontrado, ehAtual }) {
  if (!encontrado) {
    ir(destinoInicial(), { substituir: true });
    return;
  }
  const { rota, params, busca } = encontrado;
  if (rota.opcoes.livre) {
    mostrar(rota, rota.tela({ params, busca, caminho }), ehAtual);
    return;
  }
  if (rota.opcoes.publica) {
    if (sessao.usuario) {
      ir(destinoInicial(), { substituir: true });
      return;
    }
  } else {
    if (!sessao.usuario) {
      // O link "Assinar" do site: depois de entrar, volta para a assinatura.
      guardarPedido(caminho);
      ir('/entrar', { substituir: true });
      return;
    }
    if (!rota.opcoes.semEmpresa && !sessao.empresa) {
      // (Também aqui: entrou, mas ainda cadastra ou escolhe a empresa.)
      guardarPedido(caminho);
      ir(destinoInicial(), { substituir: true });
      return;
    }
    // O motorista e o agenciador ficam nas telas deles; o escritório, nas outras.
    const o = rota.opcoes;
    const deFora = sessao.papel === 'motorista' || sessao.papel === 'agenciador';
    const daqui = o.semEmpresa || (sessao.papel === 'motorista' ? o.soMotorista || o.motoristaTambem : o.soAgenciador || o.agenciadorTambem);
    if (sessao.empresa && (deFora ? !daqui : o.soMotorista || o.soAgenciador)) {
      ir(destinoInicial(), { substituir: true });
      return;
    }
  }

  if (!rota.opcoes.publica && sessao.empresa && !empresasProntas.has(chaveDaEmpresa())) {
    const chave = chaveDaEmpresa();
    app.replaceChildren(carregando('Carregando os dados…'));
    prepararEmpresa(chave).then((falha) => {
      if (!ehAtual()) return;
      if (empresasProntas.has(chave)) renderizar();
      else app.replaceChildren(telaSemDados(falha));
    });
    return;
  }

  chaveDesenhada = chaveDaConta();
  mostrar(rota, rota.tela({ params, busca, caminho }), ehAtual);
}

let manterRolagem = false;

function mostrar(rota, tela, ehAtual) {
  if (!tela || !ehAtual()) return;
  const titulo = rota.opcoes.titulo ? `${rota.opcoes.titulo} · HSA Frota` : 'HSA Frota';
  if (!manterRolagem) {
    app.replaceChildren(tela);
    document.title = titulo;
    window.scrollTo(0, 0);
    return;
  }
  // Redesenho por dados novos: monta fora da tela e troca quando estiver
  // pronta, no mesmo ponto da rolagem (sem piscar "Carregando").
  const rolagem = window.scrollY;
  const inicio = Date.now();
  const trocarQuandoPronta = () => {
    if (!ehAtual()) return;
    if (tela.querySelector('.carregando') && Date.now() - inicio < 2000) {
      setTimeout(trocarQuandoPronta, 30);
      return;
    }
    app.replaceChildren(tela);
    document.title = titulo;
    window.scrollTo(0, rolagem);
  };
  trocarQuandoPronta();
}

// Chegaram dados do servidor (outro aparelho, assinatura pelo link…): a tela
// aberta é redesenhada no mesmo lugar, a não ser que a pessoa esteja
// preenchendo algo ou que a tela se atualize sozinha (os relatórios refazem
// só as contas, sem perder o que a pessoa escolheu).
aoChegarDados(() => {
  if (!iniciado || !sessao.usuario) return;
  if (document.querySelector('form[data-alterado], dialog[open], .assinatura-quadro, [data-atualiza-sozinha]') || app.querySelector('.carregando')) return;
  const ativo = document.activeElement;
  if (ativo && ['INPUT', 'TEXTAREA', 'SELECT'].includes(ativo.tagName)) return;
  manterRolagem = true;
  try {
    renderizar();
  } finally {
    manterRolagem = false;
  }
});

let iniciado = false;

// O que a pessoa pode fazer na conta atual. Se muda (teste venceu, assinatura
// paga, papel trocado), a tela é redesenhada.
const chaveDaConta = () => `${sessao.empresa?.id}|${podeGravar()}|${sessao.empresa?.situacao}|${sessao.papel}`;
let chaveDesenhada = null;

function redesenharSeAContaMudou() {
  if (iniciado && chaveDaConta() !== chaveDesenhada) renderizar();
}

// Ao abrir, o app usa as contas guardadas no aparelho e confere com o
// servidor por trás; se algo mudou, redesenha.
quandoAContaForConferida(redesenharSeAContaMudou);

supabase.auth.onAuthStateChange((evento, sessaoAuth) => {
  // Sessão encerrada em outro lugar (ou renovação recusada): volta para a entrada.
  if (evento === 'SIGNED_OUT' && sessao.usuario) {
    setTimeout(() => {
      sessaoEncerrada();
      ir('/entrar', { substituir: true });
    }, 0);
  }
  // A sessão passou a ser de outra pessoa (entrou com outro e-mail em outra
  // aba): recomeça do zero com a pessoa certa.
  if ((evento === 'SIGNED_IN' || evento === 'TOKEN_REFRESHED') && iniciado && sessao.usuario && sessaoAuth?.user &&
    sessaoAuth.user.id !== sessao.usuario.id) {
    setTimeout(() => location.reload(), 0);
    return;
  }
  // Entrou pelo link do e-mail em outra aba: esta aba (que esperava o código)
  // segue junto. Na própria aba, quem cuida é a tela do código.
  // (O celular do motorista entra sem e-mail pela tela do link, que cuida disso.)
  if (evento === 'SIGNED_IN' && iniciado && !sessao.usuario && sessaoAuth?.user && !sessaoAuth.user.is_anonymous) {
    setTimeout(async () => {
      if (sessao.usuario) return;
      try {
        await prepararUsuario(sessaoAuth.user);
      } catch {
        /* sem internet: a próxima tela tenta de novo */
      }
      ir(destinoInicial(), { substituir: true });
    }, 0);
  }
});

// Quem toca no link do e-mail chega aqui com a sessão depois do # (o
// supabase-js entra sozinho e limpa o endereço) ou, se o link já foi usado ou
// venceu, com "#error=...". Nesse caso o app avisa e volta para a entrada.
function linkDoEmailFalhou() {
  const params = new URLSearchParams(location.hash.replace(/^#\/?/, ''));
  return params.has('error_code') || params.has('error_description');
}

// O armazenamento do aparelho falhou (Safari): fecha o banco do aparelho e
// tenta mais uma vez, antes de mostrar o erro.
async function iniciarSessaoComNovaTentativa() {
  try {
    await iniciarSessao();
  } catch (erro) {
    if (!ehErroDoArmazenamento(erro)) throw erro;
    console.warn('Armazenamento do aparelho falhou; tentando de novo.', erro);
    fecharBanco();
    await new Promise((ok) => { setTimeout(ok, 500); });
    await iniciarSessao();
  }
}

async function iniciar() {
  app.replaceChildren(carregando('Abrindo o HSA Frota…'));
  const linkFalhou = linkDoEmailFalhou();
  try {
    await iniciarSessaoComNovaTentativa();
  } catch (erro) {
    console.error(`Abertura: ${detalheDoErro(erro)}`, erro);
    // Celular do motorista que ainda não terminou de entrar: a tela do link
    // tenta de novo e mostra o motivo lá (não trava aqui).
    const telaDoLink = /^#\/motorista\//.test(location.hash) && (!sessao.usuario || sessao.usuario.is_anonymous);
    if (!telaDoLink) {
      const semRede = typeof navigator !== 'undefined' && navigator.onLine === false;
      app.replaceChildren(h('div', { class: 'entrada' },
        faixa([mensagemDeErro(erro), h('small', { class: 'detalhe-erro' }, `Detalhe: ${detalheDoErro(erro)}`)],
          'perigo', semRede ? 'Sem conexão' : 'Não deu para abrir o app'),
        h('button', { class: 'botao cheio', type: 'button', onClick: () => location.reload() }, 'Tentar de novo')));
      return;
    }
  }
  if (linkFalhou || !location.hash || location.hash === '#/' || location.hash === '#') {
    history.replaceState(null, '', `#${destinoInicial()}`);
  }
  iniciarRotas(desenhar);
  iniciado = true;
  if (linkFalhou && !sessao.usuario) {
    avisar('O link do e-mail já foi usado ou venceu. Digite seu e-mail e peça um novo.', 'erro');
  }
}

ligarSincronia({ empresa: () => sessao.empresa?.id ?? null });
iniciar();

// Quando o app volta para a tela (celular desbloqueado, aba de volta) ou o
// sinal volta, confere se a conta mudou (teste venceu, assinatura paga…) e
// redesenha se mudou o que a pessoa pode fazer.
async function conferirConta() {
  if (!sessao.usuario || !sessao.empresa) return;
  try {
    await carregarVinculos();
  } catch {
    return; // sem internet: fica como está
  }
  redesenharSeAContaMudou();
}
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') conferirConta();
});
window.addEventListener('online', conferirConta);
// O servidor recusou por falta de permissão (acesso retirado, teste vencido):
// confere a conta na hora, em vez de esperar o app voltar para a tela.
aoPerderPermissao(() => conferirConta());

// App instalado: avisa quando há versão nova, sem recarregar no meio de um cadastro.
const atualizarApp = registerSW({
  onNeedRefresh() {
    if (document.querySelector('.atualizacao')) return;
    const barra = h('div', { class: 'atualizacao', role: 'status' },
      h('div', { class: 'aviso' },
        h('span', null, 'Tem versão nova do HSA Frota.'),
        h('button', { class: 'botao secundario', type: 'button', onClick: () => atualizarApp(true) }, 'Atualizar')));
    document.body.append(barra);
  },
});
